/**
 * The transcript, as a list of pure transformations.
 *
 * Split out of the socket store so the ordering and the narration lifecycle
 * can be tested without a socket, a browser or React. They are the two places
 * in the client where a mistake is *silent*: a line in the wrong order or a
 * streamed line that never gets replaced by what the server actually stored
 * both look like history rather than like a bug.
 */

export interface RenderedMessage {
  /** Server id once confirmed; the optimistic clientId until then. */
  id: string;
  clientId?: string;
  author: string;
  text: string;
  /** `cant` is thieves' cant — the room saw that it was said, not what. */
  kind: 'player' | 'system' | 'whisper' | 'cant';
  /** Undefined while pending — the server assigns it. */
  seq?: number;
  /**
   * When the beat was actually posted, epoch ms. Undefined for a line that
   * has no stored row yet — an optimistic pending send, or the streaming
   * narration preview — which is also exactly why those two never offer the
   * Object link: nothing here says how old they are.
   */
  createdAt?: number;
  pending?: boolean;
  failed?: boolean;
  /**
   * Set aside by the table on objection.
   *
   * Still rendered — the record of a disputed call is the one thing nobody
   * should have to take on trust — but marked, and no longer objectable.
   */
  voided?: boolean;
  /**
   * A line the client built from a live event rather than from a stored row.
   *
   * Live engine results carry a synthetic id (`engine-3`, or the `actionId`) —
   * the server never sends the `messages.id` on `engine:result` — so nothing
   * that needs a real message id may be offered on one. It is not decoration:
   * an Object link here produces a refusal every time it is pressed.
   */
  live?: boolean;
}

/**
 * How long the Object link stays offered, mirroring the server's own
 * `OBJECTION_LINK_WINDOW_MS` (`apps/server/src/engine/objections.ts`).
 *
 * **A courtesy, not the gate.** The server is what actually refuses a stale
 * click; this only decides when the client stops offering one, so drift
 * between the two numbers costs an extra click-then-refusal at worst, never
 * a wrongly-accepted one.
 */
export const OBJECT_LINK_WINDOW_MS = 60_000;

/** Whether the Object link should still be offered on this message, right now. */
export function isObjectable(message: Pick<RenderedMessage, 'createdAt'>, now: number): boolean {
  return message.createdAt !== undefined && now - message.createdAt <= OBJECT_LINK_WINDOW_MS;
}

export function sortBySeq(messages: RenderedMessage[]): RenderedMessage[] {
  // Pending lines sort last: they have no seq yet and belong at the bottom,
  // where the person who just typed them expects to see them.
  return [...messages].sort(
    (a, b) => (a.seq ?? Number.MAX_SAFE_INTEGER) - (b.seq ?? Number.MAX_SAFE_INTEGER),
  );
}

/**
 * The id the streaming preview holds until the server names the real one.
 *
 * There is only ever one, because the scheduler runs one narration per beat
 * and a second beat supersedes the first.
 */
export const LIVE_NARRATION_ID = 'dm-live';

/**
 * The author narration is attributed to.
 *
 * Must match what the server persists (`appendMessage({ authorName: 'dm' })`),
 * because the same line renders from the live stream now and from the
 * transcript after a reload. A different label in the two paths would be a
 * divergence nobody notices until they scroll back.
 */
export const DM_AUTHOR = 'dm';

/** Appends one streamed chunk to the live preview, starting it if needed. */
export function appendNarrationChunk(
  messages: RenderedMessage[],
  text: string,
): RenderedMessage[] {
  const live = messages.find((m) => m.id === LIVE_NARRATION_ID);
  if (!live) {
    return sortBySeq([
      ...messages,
      {
        id: LIVE_NARRATION_ID,
        author: DM_AUTHOR,
        text,
        // `system`, not a kind of its own: the reloaded copy comes back as a
        // system line, and the two renderings have to agree.
        kind: 'system',
        pending: true,
      },
    ]);
  }
  return messages.map((m) => (m.id === LIVE_NARRATION_ID ? { ...m, text: m.text + text } : m));
}

/**
 * Drops the streaming preview.
 *
 * Called when the DM stops typing. That is *not* the end of the story: the
 * authoritative line arrives a moment later on `dm:done` and is inserted then.
 * A narration that was cancelled or superseded sends no `dm:done` at all, and
 * dropping the preview here is what stops half a sentence sitting in the
 * transcript forever.
 */
export function dropLiveNarration(messages: RenderedMessage[]): RenderedMessage[] {
  return messages.filter((m) => m.id !== LIVE_NARRATION_ID);
}

/**
 * Commits the narration the server persisted.
 *
 * **The text comes from `dm:done`, never from the accumulated chunks.** A
 * stall, a provider failure or an exhausted budget substitutes a terse
 * fallback for whatever streamed — and the budget case streams nothing at all
 * — so the chunks are a preview and this is the record.
 */
export function commitNarration(
  messages: RenderedMessage[],
  narration: { id: string; text: string; seq: number; createdAt: number },
): RenderedMessage[] {
  const withoutLive = dropLiveNarration(messages);
  if (withoutLive.some((m) => m.id === narration.id)) return withoutLive;
  return sortBySeq([
    ...withoutLive,
    {
      id: narration.id,
      author: DM_AUTHOR,
      text: narration.text,
      kind: 'system',
      seq: narration.seq,
      createdAt: narration.createdAt,
    },
  ]);
}
